import { useEffect, useRef, type RefObject } from 'react'

// MP-OS-188 bug #4 (round 3) — ONE Escape/focus behaviour for every popover, dropdown and
// menu in the app, instead of a per-component keydown handler that only saw keys bubbling
// up from inside the panel. Picking an option can move focus to <body> (a native button
// click, or the caller disabling the surrounding <fieldset> while the next request loads),
// and a panel-level handler never hears that Escape. So:
//
// - While open (and only then) a document-level keydown listener closes on Escape, wherever
//   focus is. `close` must only close; it never touches the confirmed choice.
// - Focus goes back to the trigger. A disabled trigger cannot take focus, so the request
//   is remembered and finished once it is enabled again, and only if focus is still nowhere.
// - Focus is never taken from where the user went next: on Escape it is only moved when it
//   sits on <body>, the trigger or inside `containerRef` (the popover's own markup).
// - Nested popovers: only the most recently opened one reacts to an Escape.
// - An Escape another handler already handled (defaultPrevented, e.g. a modal dialog such as
//   TrendPopup that closes itself on Escape) is left alone, so one Escape closes one thing.
// - Outside click stays with each component.

const openStack: object[] = []

const focusIsNowhere = () => !document.activeElement || document.activeElement === document.body

export function usePopoverDismiss({
  open,
  close,
  triggerRef,
  containerRef,
}: {
  open: boolean
  close: () => void
  triggerRef: RefObject<HTMLElement | null>
  containerRef?: RefObject<HTMLElement | null>
}) {
  const focusPending = useRef(false)
  // True while the hook itself moves focus to the trigger, so a trigger that opens on focus
  // (a combobox input) can tell a restore from the user focusing it.
  const restoring = useRef(false)

  const focusTrigger = () => {
    const trigger = triggerRef.current
    if (!trigger) return
    if (trigger.matches(':disabled')) { focusPending.current = true; return }
    focusPending.current = false
    if (document.activeElement === trigger) return
    restoring.current = true
    try { trigger.focus() } finally { restoring.current = false }
  }

  const userIsHere = () => {
    const active = document.activeElement
    return focusIsNowhere() || active === triggerRef.current || !!(active && containerRef?.current?.contains(active))
  }

  // Close and return focus to the trigger (unless the user has moved on).
  const dismiss = () => {
    const returnFocus = userIsHere()
    close()
    if (returnFocus) focusTrigger()
  }
  const dismissRef = useRef(dismiss)
  dismissRef.current = dismiss

  // Finish a deferred focus once the trigger is enabled — runs after every render.
  useEffect(() => {
    if (!focusPending.current || !triggerRef.current || triggerRef.current.matches(':disabled')) return
    focusPending.current = false
    if (!focusIsNowhere()) return
    restoring.current = true
    try { triggerRef.current.focus() } finally { restoring.current = false }
  })

  useEffect(() => {
    if (!open) return
    const token = {}
    openStack.push(token)
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || openStack[openStack.length - 1] !== token) return
      e.preventDefault()
      dismissRef.current()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      const i = openStack.indexOf(token)
      if (i >= 0) openStack.splice(i, 1)
    }
  }, [open])

  return { dismiss, isRestoringFocus: () => restoring.current }
}
